/**
 * Authentication Context for global auth state
 *
 * Uses httpOnly cookies for secure token storage
 */
import { devError } from '../utils/devLog';
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService, User } from '../services/AuthService';
import { cacheAvatar, clearExpiredCache } from '../utils/avatarCache';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  completeLogin: (code: string, inviteCode: string, turnstileToken: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Load user on mount and clear expired cache
  useEffect(() => {
    clearExpiredCache();
    loadUser();
  }, []);

  const loadUser = async () => {
    try {
      // Check auth status via httpOnly cookie
      const authStatus = await authService.checkAuth();

      if (!authStatus.authenticated) {
        setLoading(false);
        return;
      }

      // User is authenticated, get full profile
      const userData = await authService.getMe();

      // Cache avatar if present
      if (userData.avatar) {
        const cachedAvatarUrl = await cacheAvatar(userData.avatar);
        userData.avatar = cachedAvatarUrl;
      }

      setUser(userData);
    } catch (error) {
      devError('Failed to load user:', error);
      // Auth check failed - user is not authenticated
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } finally {
      setUser(null);
    }
  };

  const refreshUser = async () => {
    try {
      // Check if still authenticated
      const authStatus = await authService.checkAuth();
      if (!authStatus.authenticated) {
        setUser(null);
        return;
      }

      const userData = await authService.getMe();

      // Cache avatar if present
      if (userData.avatar) {
        const cachedAvatarUrl = await cacheAvatar(userData.avatar);
        userData.avatar = cachedAvatarUrl;
      }

      setUser(userData);
    } catch (error) {
      devError('Failed to refresh user:', error);
    }
  };

  const completeLogin = async (code: string, inviteCode: string, turnstileToken: string) => {
    const response = await authService.completeLogin(code, inviteCode, turnstileToken);

    // Cache avatar if present
    if (response.user.avatar) {
      const cachedAvatarUrl = await cacheAvatar(response.user.avatar);
      response.user.avatar = cachedAvatarUrl;
    }

    setUser(response.user);
  };

  const value: AuthContextType = {
    user,
    loading,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    logout,
    refreshUser,
    completeLogin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
